import { Component } from '@lisergia/core'
import { Viewport } from '@lisergia/managers'
import { MathUtils } from '@lisergia/utilities'

import GSAP from 'gsap'
import sample from 'lodash/sample'
import * as Matter from 'matter-js'

const Engine = Matter.Engine
const Render = Matter.Render
const Runner = Matter.Runner
const Composites = Matter.Composites
const MouseConstraint = Matter.MouseConstraint
const Mouse = Matter.Mouse
const Composite = Matter.Composite
const Bodies = Matter.Bodies

export default class extends Component {
  declare element: HTMLDivElement
  declare elements: {
    number: HTMLDivElement
  }

  number = 0

  engine: any
  render: any
  runner: any

  constructor({ element }: { element: HTMLElement }) {
    super({
      autoListeners: false,
      element,
      elements: {
        number: '.activities__number',
      },
    })

    this.createMatter()

    this.addEventListeners()

    window
      .fetch('https://www.ageoflearning.com/php/activities.php')
      .then(async (data) => {
        const json = await data.json()

        this.number = Number(json.total)

        this.onAddPoints()
      })
      .catch((error) => {
        this.number = Number(this.elements.number.innerHTML.trim().replace(/,/g, ''))

        this.onAddPoints()
      })
  }

  bodies: Matter.Body[] = []
  walls: Matter.Body[] = []

  createMatter() {
    this.engine = Engine.create()

    const world = this.engine.world

    const height = this.element.clientHeight
    const width = this.element.clientWidth

    this.render = Render.create({
      element: this.element,
      engine: this.engine,
      options: {
        background: 'transparent',
        height,
        showAngleIndicator: false,
        width,
        wireframes: false,
      },
    })

    Render.run(this.render)

    this.runner = Runner.create()

    Runner.run(this.runner, this.engine)

    world.bodies = []

    const textures = [
      { path: `${window.VARIABLES.THEME}/images/activities/2-pts.png`, height: 75, width: 75, shape: 'circle' },
      { path: `${window.VARIABLES.THEME}/images/activities/3-pts.png`, height: 75, width: 75, shape: 'circle' },
      { path: `${window.VARIABLES.THEME}/images/activities/5-pts.png`, height: 75, width: 75, shape: 'circle' },
      {
        path: `${window.VARIABLES.THEME}/images/activities/bubble-popper.png`,
        height: 70,
        width: 253,
        shape: 'rectangle',
      },
      { path: `${window.VARIABLES.THEME}/images/activities/circle.png`, height: 122, width: 122, shape: 'circle' },
      { path: `${window.VARIABLES.THEME}/images/activities/classroom.png`, height: 70, width: 199, shape: 'rectangle' },
      {
        path: `${window.VARIABLES.THEME}/images/activities/letter-runner.png`,
        height: 70,
        width: 236,
        shape: 'rectangle',
      },
      {
        path: `${window.VARIABLES.THEME}/images/activities/math-skill.png`,
        height: 70,
        width: 187,
        shape: 'rectangle',
      },
      {
        path: `${window.VARIABLES.THEME}/images/activities/monster-stack.png`,
        height: 70,
        width: 245,
        shape: 'rectangle',
      },
      {
        path: `${window.VARIABLES.THEME}/images/activities/music-game.png`,
        height: 70,
        width: 222,
        shape: 'rectangle',
      },
      {
        path: `${window.VARIABLES.THEME}/images/activities/reading-activity.png`,
        height: 70,
        width: 267,
        shape: 'rectangle',
      },
      {
        path: `${window.VARIABLES.THEME}/images/activities/semi-circle.png`,
        height: 86,
        width: 166,
        shape: 'semi-circle',
      },
      { path: `${window.VARIABLES.THEME}/images/activities/rectangle.png`, height: 59, width: 253, shape: 'rectangle' },
    ]

    this.bodies = textures.map((texture) => {
      const x = MathUtils.random(width / 2 - 300, width / 2 + 300)
      const y = -100

      let body: Matter.Body

      const ratio = Viewport.isDesktop ? Math.min(Viewport.width / 1728, 1) : (Viewport.width / 402) * 0.5
      const scale = 0.33333 * ratio

      if (texture.shape === 'rectangle') {
        body = Bodies.rectangle(x, y, texture.width * ratio, texture.height * ratio, {
          render: {
            sprite: {
              texture: texture.path,
              xScale: scale,
              yScale: scale,
            },
          },
        })
      } else if (texture.shape === 'semi-circle') {
        body = Bodies.rectangle(x, y, texture.width * ratio, texture.height * ratio, {
          render: {
            sprite: {
              texture: texture.path,
              xScale: scale,
              yScale: scale,
            },
          },
        })
      } else if (texture.shape === 'square') {
        body = Bodies.rectangle(x, y, texture.width * ratio, texture.height * ratio, {
          render: {
            sprite: {
              texture: texture.path,
              xScale: scale,
              yScale: scale,
            },
          },
        })
      } else if (texture.shape === 'circle') {
        body = Bodies.circle(x, y, (texture.width * ratio) / 2, {
          render: {
            sprite: {
              texture: texture.path,
              xScale: scale,
              yScale: scale,
            },
          },
        })
      }

      Composite.add(world, body)

      return body
    })

    const mouse = Mouse.create(this.render.canvas)
    const mouseConstraint = MouseConstraint.create(this.engine, {
      mouse: mouse,
      constraint: {
        stiffness: 0.5,
        render: {
          visible: false,
        },
      },
    })

    Composite.add(world, mouseConstraint)

    this.render.mouse = mouse
  }

  numberWithCommas(x: string) {
    return x.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  onAnimateIn() {
    const width = this.element.clientWidth
    const height = this.element.clientHeight

    this.bodies.forEach((body: any) => {
      Matter.Body.setPosition(body, {
        x: MathUtils.random(width / 2 - 50, width / 2 + 50),
        y: -100,
      })

      Matter.Body.setVelocity(body, { x: 0, y: 0 })
    })
  }

  onAddPoints() {
    const entry = sample([
      { path: `${window.VARIABLES.THEME}/images/activities/2-pts.png`, height: 75, width: 75, shape: 'circle' },
      { path: `${window.VARIABLES.THEME}/images/activities/3-pts.png`, height: 75, width: 75, shape: 'circle' },
      { path: `${window.VARIABLES.THEME}/images/activities/5-pts.png`, height: 75, width: 75, shape: 'circle' },
    ])

    const extra = entry.path.includes('2-pts') ? 2 : entry.path.includes('3-pts') ? 3 : 5
    const number = this.number + extra

    this.number = number

    GSAP.fromTo(
      this.elements.number,
      { textContent: number },
      {
        duration: 1,
        ease: 'linear',
        onUpdate: () => {
          this.elements.number.innerHTML = this.numberWithCommas(this.elements.number.textContent)
        },
        onComplete: () => {
          this.onAddPoints()
        },
        snap: {
          textContent: 1,
        },
        textContent: number + extra,
      },
    )

    const height = this.element.clientHeight
    const width = this.element.clientWidth

    const x = MathUtils.random(width / 2 - 50, width / 2 + 50)
    const y = -100

    const ratio = Viewport.isDesktop ? Math.min(Viewport.width / 1728, 1) : (Viewport.width / 402) * 0.5
    const scale = 0.33333 * ratio

    const body = Bodies.circle(x, y, (entry.width * ratio) / 2, {
      render: {
        sprite: {
          texture: entry.path,
          xScale: scale,
          yScale: scale,
        },
      },
    })

    Composite.add(this.engine.world, body)

    GSAP.delayedCall(MathUtils.random(5, 10), () => {
      if (body.render.sprite) {
        GSAP.to(body.render.sprite, {
          duration: 1,
          ease: 'expo.inOut',
          xScale: 0,
          yScale: 0,
          onComplete: () => {
            Composite.remove(this.engine.world, body)
          },
        })
      }
    })
  }

  declare intersectionObserver: IntersectionObserver

  createIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.onAnimateIn()
        }
      })
    })

    this.intersectionObserver.observe(this.element)
  }

  destroyIntersectionObserver() {
    this.intersectionObserver.disconnect()
  }

  onResize() {
    const width = this.element.clientWidth
    const height = this.element.clientHeight

    Render.setSize(this.render, width, height)

    Composite.remove(this.engine.world, this.walls)

    const wallThickness = 20
    const options = {
      render: {
        fillStyle: 'transparent',
        strokeStyle: 'transparent',
        lineWidth: 3,
      },
      isStatic: true,
    }

    this.walls = [
      // Bottom wall
      Bodies.rectangle(width / 2, height + wallThickness / 2, width + wallThickness * 2, wallThickness, options),
      // Left wall
      Bodies.rectangle(-wallThickness / 2, height / 2, wallThickness, height + wallThickness * 2, options),
      // Right wall
      Bodies.rectangle(width + wallThickness / 2, height / 2, wallThickness, height + wallThickness * 2, options),
    ]

    Composite.add(this.engine.world, this.walls)

    setTimeout(() => {
      Render.lookAt(this.render, this.walls, {
        x: 0,
        y: 0,
      })
    }, 100)
  }

  addEventListeners() {
    this.createIntersectionObserver()

    Viewport.on('resize', this.onResize)
  }

  removeEventListeners() {
    this.destroyIntersectionObserver()

    Viewport.off('resize', this.onResize)
  }

  destroy() {
    super.destroy()

    Matter.Render.stop(this.render)
    Matter.Runner.stop(this.runner)
  }
}
